// hooks/useRecurrence.js
import { useState, useEffect, useCallback } from "react";
import { managementService } from "../services/managementService.js";

/**
 * Hook pour gérer le CRUD des récurrences
 */
export function useRecurrence(eventId = null) {
  const [recurrence, setRecurrence] = useState(null);
  const [pendingRecurrence, setPendingRecurrence] = useState(null);
  const [draftRecurrence, setDraftRecurrence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Indicateur de modifications en attente
  const hasDraft = draftRecurrence !== null;

  // Charger la récurrence si eventId existe
  useEffect(() => {
    if (!eventId) {
      setRecurrence(null);
      return;
    }

    const fetchRecurrence = async () => {
      setLoading(true);
      setError(null);
      try {
        const recurrences = await managementService.getAllRecurrences(eventId);
        if (recurrences && recurrences.length > 0) {
          setRecurrence(recurrences[0]);
        } else {
          setRecurrence(null);
        }
      } catch (err) {
        setError(err.message);
        console.error("Erreur chargement récurrence:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecurrence();
  }, [eventId]);

  // Mettre à jour le brouillon de récurrence
  const updateDraft = useCallback(
    (recurrenceData) => {
      if (recurrenceData === null) {
        // Annulation
        setDraftRecurrence(null);
        if (!eventId) {
          setRecurrence(null);
          setPendingRecurrence(null);
        } else {
          // En mode édition, restaurer la récurrence originale
          setRecurrence((prev) => (prev?.id ? prev : null));
        }
      } else {
        // Toujours inclure l'id de la récurrence existante si elle existe
        const draftWithId = {
          ...recurrenceData,
          id: recurrence?.id, // CORRECTION: Toujours préserver l'id
        };

        setDraftRecurrence(draftWithId);

        if (!eventId) {
          // En mode création, marquer comme pending
          setRecurrence({ ...recurrenceData, isPending: true });
          setPendingRecurrence(recurrenceData);
        } else {
          // En mode édition, marquer la récurrence avec les nouvelles données
          setRecurrence(draftWithId);
        }
      }
    },
    [eventId, recurrence],
  );

  // Valider la récurrence
  const validateRecurrence = useCallback((data) => {
    if (data.frequency === "weekly") {
      if (!data.week_days || data.week_days.length === 0) {
        throw new Error(
          "Veuillez sélectionner au moins un jour pour une récurrence hebdomadaire",
        );
      }
    }
    return true;
  }, []);

  // Sauvegarder la récurrence (appelée lors de la sauvegarde du formulaire principal)
  const saveRecurrence = useCallback(
    async (slotData) => {
      const dataToSave = draftRecurrence || recurrence;

      // Pas de données à sauvegarder
      if (!dataToSave) {
        return { isPending: false, data: null };
      }

      // Valider avant de sauvegarder
      try {
        validateRecurrence(dataToSave);
      } catch (validationError) {
        setError(validationError.message);
        throw validationError;
      }

      // Mode création : pas d'eventId encore
      if (!eventId) {
        console.warn(
          "saveRecurrence appelé sans eventId - utiliser createPendingRecurrence à la place",
        );
        return { isPending: true, data: dataToSave };
      }

      // Mode édition : eventId existe
      setSaving(true);
      setError(null);
      try {
        const payload = {
          frequency: dataToSave.frequency,
          interval: dataToSave.interval || 1,
          week_days:
            dataToSave.frequency === "weekly" ? dataToSave.week_days : [],
          is_all_day: slotData?.is_all_day || false,
          start_time:
            slotData?.is_all_day || !slotData?.start_time
              ? null
              : slotData.start_time,
          end_time:
            slotData?.is_all_day || !slotData?.end_time
              ? null
              : slotData.end_time,
          event_id: eventId,
        };

        let savedRecurrence;

        // CORRECTION: Vérifier l'id de manière plus robuste
        if (dataToSave.id) {
          // Mise à jour d'une récurrence existante
          console.log("Mise à jour récurrence existante, id:", dataToSave.id);
          savedRecurrence = await managementService.updateRecurrence(
            dataToSave.id,
            payload,
          );
        } else {
          // Création d'une nouvelle récurrence en mode édition
          console.log("Création nouvelle récurrence en mode édition");
          savedRecurrence = await managementService.createRecurrence(payload);
        }

        setRecurrence(savedRecurrence);
        setPendingRecurrence(null);
        setDraftRecurrence(null);
        return { isPending: false, data: savedRecurrence };
      } catch (err) {
        const errorMessage =
          err.message || "Erreur lors de la sauvegarde de la récurrence";
        setError(errorMessage);
        console.error("Erreur sauvegarde récurrence:", err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [eventId, recurrence, draftRecurrence, validateRecurrence],
  );

  // Supprimer
  const deleteRecurrence = useCallback(
    async (recurrenceId) => {
      // Si c'est une récurrence pending (pas encore créée)
      if (recurrence?.isPending) {
        setRecurrence(null);
        setPendingRecurrence(null);
        setDraftRecurrence(null);
        return;
      }

      setDeleting(true);
      setError(null);
      try {
        await managementService.deleteRecurrence(recurrenceId);
        setRecurrence(null);
        setPendingRecurrence(null);
        setDraftRecurrence(null);
      } catch (err) {
        const errorMessage =
          err.message || "Erreur lors de la suppression de la récurrence";
        setError(errorMessage);
        console.error("Erreur suppression récurrence:", err);
        throw err;
      } finally {
        setDeleting(false);
      }
    },
    [recurrence],
  );

  // Créer la récurrence pending après création de l'event
  const createPendingRecurrence = useCallback(
    async (createdEventId, slotData) => {
      if (!pendingRecurrence) return null;

      // Valider avant de créer
      try {
        validateRecurrence(pendingRecurrence);
      } catch (validationError) {
        setError(validationError.message);
        throw validationError;
      }

      setSaving(true);
      setError(null);
      try {
        const payload = {
          frequency: pendingRecurrence.frequency,
          interval: pendingRecurrence.interval || 1,
          week_days:
            pendingRecurrence.frequency === "weekly"
              ? pendingRecurrence.week_days
              : [],
          is_all_day: slotData?.is_all_day || false,
          start_time:
            slotData?.is_all_day || !slotData?.start_time
              ? null
              : slotData.start_time,
          end_time:
            slotData?.is_all_day || !slotData?.end_time
              ? null
              : slotData.end_time,
          event_id: createdEventId,
        };

        console.log("Création récurrence pending:", payload);
        const savedRecurrence =
          await managementService.createRecurrence(payload);
        setRecurrence(savedRecurrence);
        setPendingRecurrence(null);
        setDraftRecurrence(null);
        return savedRecurrence;
      } catch (err) {
        const errorMessage =
          err.message || "Erreur lors de la création de la récurrence";
        setError(errorMessage);
        console.error("Erreur création récurrence:", err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [pendingRecurrence, validateRecurrence],
  );

  return {
    recurrence,
    pendingRecurrence,
    hasDraft,
    loading,
    saving,
    deleting,
    error,
    updateDraft,
    saveRecurrence,
    deleteRecurrence,
    createPendingRecurrence,
  };
}
